export interface UserDashboardStats {
  user_info: {
    id: number;
    name: string;
    email: string;
    firm: string;
  };
  period: {
    type: string;
    start_date: string;
    end_date: string;
  };
  summary: {
    total_hours: string;
    billable_hours: string;
    utilization_rate: number;
    total_revenue?: string;
    average_rate?: string;
    days_worked: number;
    efficiency_score: number;
  };
  targets: {
    yearly_target: number;
    ytd_billable_hours: number;
    target_progress_percentage: number;
    hours_ahead_behind: number;
    monthly_target?: number;
    quarterly_target?: number;
  };
  // Legacy compatibility fields (computed from new structure)
  hours_table?: {
    billable: {
      day: number;
      month: number;
      ytd: number;
    };
    non_billable: {
      day: number;
      month: number;
      ytd: number;
    };
    total: {
      day: number;
      month: number;
      ytd: number;
    };
  };
  utilization_rate?: number; // alias for summary.utilization_rate
  active_clients_count?: number;
  frequent_matters?: Array<{
    id: number;
    name: string;
    hours: number;
  }>;
  monthly_target?: number; // alias for targets.monthly_target
  yearly_target?: number; // alias for targets.yearly_target
  target_progress?: number; // alias for targets.target_progress_percentage
  target_diff?: number; // alias for targets.hours_ahead_behind
  current_timer_hours?: number;
}

export interface FirmDashboardStats {
  period: {
    type: string;
    start_date: string;
    end_date: string;
  };
  summary: {
    total_revenue?: string;
    billable_hours: string;
    utilization_rate: number;
    active_clients: number;
    active_matters: number;
    active_users: number;
    wip_value?: string;
    collection_rate?: number;
  };
  trends?: {
    revenue_change?: number;
    revenue_direction?: 'up' | 'down' | 'stable';
    hours_change?: number;
    hours_direction?: 'up' | 'down' | 'stable';
  };
  // Legacy compatibility fields (computed from new structure)
  team_utilization?: number; // alias for summary.utilization_rate
  total_billable_hours?: number; // alias for summary.billable_hours (parsed as number)
  revenue?: number; // alias for summary.total_revenue (parsed as number)
  avg_rate_per_billable_hour?: number;
  month_revenue?: number;
  month_revenue_change?: number;
  utilization_rate?: number; // alias for summary.utilization_rate
  total_hours_today?: number;
  outstanding_invoices?: number;
  unbilled_hours?: number;
  user_stats?: Array<{
    user_id: number;
    user_name: string;
    user_email: string;
    hours_table: {
      billable: { day: number; month: number; ytd: number; };
      non_billable: { day: number; month: number; ytd: number; };
      total: { day: number; month: number; ytd: number; };
    };
    yearly_target: number;
    monthly_target: number;
    target_progress: number;
    target_diff: number;
    utilization_rate: number;
  }>;
}

export interface BillingDashboardStats {
  period: string;
  time_entries: {
    total: {
      hours: number;
      revenue: number;
    };
    wip: {
      hours: number;
      revenue: number;
      hours_percent: number;
      revenue_percent: number;
    };
    invoiced: {
      hours: number;
      revenue: number;
      hours_percent: number;
      revenue_percent: number;
    };
    paid: {
      hours: number;
      revenue: number;
      hours_percent: number;
      revenue_percent: number;
    };
  };
  expenses: {
    total: number;
    wip: {
      amount: number;
      percent: number;
    };
    invoiced: {
      amount: number;
      percent: number;
    };
    paid: {
      amount: number;
      percent: number;
    };
  };
  additional_metrics: {
    avg_days_to_payment: number;
    invoices_overdue: number;
    collection_rate: number;
  };
}

export interface TimeEntryRow {
  id?: number;
  matter: number;
  /**
   * Optional manual phase override. Null = attribute to the matter phase
   * whose date range covers this entry's date (or unphased).
   */
  phase?: number | null;
  description: string;
  hours: string;
  billable: boolean;
  isNew?: boolean;
  isEditing?: boolean;
  timer_active?: boolean;
  matterName?: string;
  originalValues?: {
    matter: number;
    phase?: number | null;
    description: string;
    hours: string;
    billable: boolean;
  };
}

export interface DashboardSettings {
  // Time formatting
  timeFormat: 'decimal' | 'hhmm'; // 1.5 vs 1:30
  hoursRounding: 0 | 1 | 2; // decimal places
  
  // Colors and themes
  theme: 'default' | 'modern' | 'minimal';
  accentColor: string;
  timerActiveColor: string;
  progressColors: {
    poor: string;
    fair: string;
    good: string;
    excellent: string;
    outstanding: string;
  };
  
  // Layout preferences
  compactMode: boolean;
  showSeconds: boolean;
  autoSaveDelay: number; // milliseconds
  
  // Target display
  showDailyTargets: boolean;
  showMonthlyTargets: boolean;
  showYearlyTargets: boolean;
  targetCalculationMethod: 'calendar' | 'workdays';
  
  // Timer behavior
  timerAutoStart: boolean;
  timerConfirmStop: boolean;
  timerSoundEnabled: boolean;
  timerNotifications: boolean;
  
  // Data visibility
  showTeamStats: boolean;
  showBillingStats: boolean;
  defaultBillingPeriod: 'monthly' | 'yearly';
  
  // Entry defaults
  defaultBillable: boolean;
  requireMatterSelection: boolean;
  requireDescription: boolean;
  minHoursEntry: number;
  maxHoursEntry: number;
  
  // Grid and table settings
  rowsPerPage: number;
  enableInlineEditing: boolean;
  showGridLines: boolean;
  
  // Currency and locale
  currency: string;
  locale: string;
  dateFormat: string;
}

export const DEFAULT_DASHBOARD_SETTINGS: DashboardSettings = {
  timeFormat: 'hhmm',
  hoursRounding: 2,
  theme: 'default',
  accentColor: '#2563eb',
  timerActiveColor: '#10b981',
  progressColors: {
    poor: '#ef4444',
    fair: '#f97316', 
    good: '#eab308',
    excellent: '#10b981',
    outstanding: '#8b5cf6'
  },
  compactMode: false,
  showSeconds: false,
  autoSaveDelay: 800,
  showDailyTargets: true,
  showMonthlyTargets: true,
  showYearlyTargets: true,
  targetCalculationMethod: 'calendar',
  timerAutoStart: false,
  timerConfirmStop: true,
  timerSoundEnabled: true,
  timerNotifications: true,
  showTeamStats: true,
  showBillingStats: true,
  defaultBillingPeriod: 'monthly',
  defaultBillable: true,
  requireMatterSelection: true,
  requireDescription: true,
  minHoursEntry: 0,
  maxHoursEntry: 24,
  rowsPerPage: 10,
  enableInlineEditing: true,
  showGridLines: true,
  currency: 'EUR',
  locale: 'en-US',
  dateFormat: 'EEEE, d MMMM yyyy'
};
